import { Box } from '@mui/material';
import type { FieldMode } from '../../hooks/useCaseField';
import { CaseTextField } from '../fields';
import type { MedNecCaseView, PatientView } from '../../../../services/MedNecService/types/medNecTypes';

interface PatientSectionProps {
  mode: FieldMode;
  errors?: { [key: string]: string | undefined };
}

// Helper: read a patient field, falling back to ''
const p = (field: keyof PatientView) => (d: MedNecCaseView) => (d.patient?.[field] as string) ?? '';

// Helper: produce an update that merges a single patient field
const pUp = (field: keyof PatientView) => (v: string, d: MedNecCaseView): Partial<MedNecCaseView> => ({
  patient: { ...d.patient!, [field]: v },
});

const PatientSection: React.FC<PatientSectionProps> = ({ mode, errors = {} }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 1fr', gap: 1.5, mb: 1.5 }}>
      <CaseTextField mode={mode} label="First Name" required error={errors.firstName}
        getValue={p('firstName')} getUpdate={pUp('firstName')} />
      <CaseTextField mode={mode} label="Last Name" required error={errors.lastName}
        getValue={p('lastName')} getUpdate={pUp('lastName')} />
      <CaseTextField mode={mode} label="MI" inputProps={{ maxLength: 1 }}
        getValue={p('middleInitial')} getUpdate={pUp('middleInitial')} />
      <CaseTextField mode={mode} label="Date of Birth" type="date" required shrinkLabel error={errors.dateOfBirth}
        getValue={d => { const v = p('dateOfBirth')(d); return v.includes('T') ? v.split('T')[0] : v; }}
        getUpdate={pUp('dateOfBirth')} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 1.5, mb: 1.5 }}>
      <CaseTextField mode={mode} label="Member ID" required error={errors.memberId}
        getValue={p('memberId')} getUpdate={pUp('memberId')} />
      <CaseTextField mode={mode} label="Sex" getValue={p('sex')} getUpdate={pUp('sex')} />
      <CaseTextField mode={mode} label="Home Phone" getValue={p('homePhone')} getUpdate={pUp('homePhone')} />
      <CaseTextField mode={mode} label="Cell Phone" getValue={p('cellPhone')} getUpdate={pUp('cellPhone')} />
    </Box>
    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 120px', gap: 1.5 }}>
      <CaseTextField mode={mode} label="Address" getValue={p('address')} getUpdate={pUp('address')} />
      <CaseTextField mode={mode} label="City" getValue={p('city')} getUpdate={pUp('city')} />
      <CaseTextField mode={mode} label="State" getValue={p('state')} getUpdate={pUp('state')} />
      <CaseTextField mode={mode} label="Zip" getValue={p('zip')} getUpdate={pUp('zip')} />
    </Box>
  </Box>
);

export default PatientSection;
